import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { getSurvey, createSurvey, updateSurvey, importDocAsSurvey } from '../lib/api';
import type { SurveyConfig, QuestionType, Question, JumpTarget } from '../components/editor/types';
import { titleText } from '../components/editor/types';
import QuestionCard from '../components/editor/QuestionCard';
import AddQuestionModal from '../components/editor/AddQuestionModal';
import SettingsPanel from '../components/editor/SettingsPanel';
import PreviewPanel from '../components/editor/PreviewPanel';
import { pageNumberMap, displayIndexMap, cloneQuestion, pageRangeBefore } from '../components/editor/question-utils';

type Mode = 'new' | 'edit';

type Props = {
  mode: Mode;
};

type Tab = 'questions' | 'preview';

function blankSurvey(id: string): SurveyConfig {
  return {
    id,
    header: { mainTitle: '问卷', subTitle: '' },
    questions: [],
    settings: {},
  };
}

function nextQuestionIdFrom(ids: Set<string>): string {
  let n = 1;
  while (ids.has(`Q${n}`)) n += 1;
  return `Q${n}`;
}

function nextQuestionId(config: SurveyConfig): string {
  return nextQuestionIdFrom(new Set(config.questions.map((q) => q.id)));
}

function makeQuestion(type: QuestionType, id: string): Question {
  const base = {
    id,
    type,
    title: { text: '', format: {} as Record<string, undefined> },
    description: '',
    required: type === 'statement' || type === 'pagebreak' ? false : true,
    displayLogic: { operator: 'AND' as const, conditions: [] },
  };
  if (type === 'single' || type === 'multi' || type === 'select') {
    return { ...base, options: [{ value: '选项1' }, { value: '选项2' }] };
  }
  if (type === 'ranking') {
    // 0903 排序题：填写者点击选项即排到第 1 位；选数限制放 params（与多选题同名 minSelect / maxSelect）
    return { ...base, options: [{ value: '选项1' }, { value: '选项2' }, { value: '选项3' }], params: {} };
  }
  if (type === 'slider') {
    return { ...base, params: { min: 1, max: 15, step: 1, allowDecimal: true, minLabel: '', maxLabel: '' } };
  }
  if (type === 'longtext') {
    return { ...base, params: { maxLen: 500 } };
  }
  if (type === 'matrix') {
    return { ...base, params: { rows: ['项目1', '项目2'], columns: ['满意', '一般', '不满意'] } };
  }
  if (type === 'matrixtext') {
    // 0902 矩阵填空：左列行标题，右列填写者逐行填文本
    return { ...base, params: { rows: ['行1', '行2'] } };
  }
  if (type === 'matrixslider') {
    // 0907 矩阵滑动题：每行一个独立滑动条，分数范围共用（默认 0-100 整数）
    return { ...base, params: { rows: ['项目1', '项目2'], min: 0, max: 100, step: 1, allowDecimal: false, minLabel: '', maxLabel: '' } };
  }
  // statement / pagebreak：仅基础字段
  return base;
}

/** 删题后清理其它题对被删题的引用：显示逻辑条件、题目关联、选项关联（0907）、跳题目标 */
function dropQuestionRefs(q: Question, removedIds: Set<string>): Question {
  const next: Question = {
    ...q,
    displayLogic: {
      ...(q.displayLogic ?? { operator: 'AND', conditions: [] }),
      conditions: (q.displayLogic?.conditions ?? []).filter((cond) => !removedIds.has(cond.question)),
    },
  };
  if (Array.isArray(q.correlation)) {
    const kept = q.correlation.filter((c) => !removedIds.has(c.questionId));
    next.correlation = kept.length ? kept : undefined;
  }
  if (Array.isArray(q.optionLinks)) {
    const kept = q.optionLinks.filter((l) => !removedIds.has(l.questionId));
    next.optionLinks = kept.length ? kept : undefined;
  }
  if (q.jumpLogic) {
    const jl = { ...q.jumpLogic };
    if (jl.byOption) {
      const bo: Record<string, JumpTarget> = {};
      for (const [k, t] of Object.entries(jl.byOption)) if (!(t.kind === 'question' && removedIds.has(t.qid))) bo[k] = t;
      jl.byOption = Object.keys(bo).length ? bo : undefined;
    }
    if (jl.unconditional?.kind === 'question' && removedIds.has(jl.unconditional.qid)) jl.unconditional = undefined;
    next.jumpLogic = jl.byOption || jl.unconditional ? jl : undefined;
  }
  return next;
}

function optionValuesOf(options: Question['options'] | undefined): string[] {
  return (options ?? []).map((o) => (typeof o === 'string' ? o : (o as any)?.value)).filter((v): v is string => typeof v === 'string');
}

/**
 * 源题选项改名 / 删除后，同步其它题里引用该选项的规则：
 *  - 选项关联 optionLinks[].optionValue（0907）与题目关联 correlation[].optionValue（1627）
 *  - 等长视为改名（按位置对应），长度变化视为删除（旧值不在新值里的引用去掉）
 */
function syncOptionRefs(questions: Question[], srcId: string, oldVals: string[], newVals: string[]): Question[] {
  const rename = new Map<string, string>();
  const removed = new Set<string>();
  if (oldVals.length === newVals.length) {
    oldVals.forEach((v, i) => { if (v !== newVals[i]) rename.set(v, newVals[i]); });
  } else {
    for (const v of oldVals) if (!newVals.includes(v)) removed.add(v);
  }
  if (rename.size === 0 && removed.size === 0) return questions;
  const fix = <T extends { questionId: string; optionValue: string }>(list: T[]): T[] =>
    list
      .filter((x) => x.questionId !== srcId || !removed.has(x.optionValue))
      .map((x) => (x.questionId === srcId && rename.has(x.optionValue) ? { ...x, optionValue: rename.get(x.optionValue)! } : x));
  return questions.map((q) => {
    const touchLinks = Array.isArray(q.optionLinks) && q.optionLinks.some((l) => l.questionId === srcId);
    const touchCorr = Array.isArray(q.correlation) && q.correlation.some((c) => c.questionId === srcId);
    if (!touchLinks && !touchCorr) return q;
    const next = { ...q };
    if (touchLinks) { const l = fix(q.optionLinks!); next.optionLinks = l.length ? l : undefined; }
    if (touchCorr) { const c = fix(q.correlation!); next.correlation = c.length ? c : undefined; }
    return next;
  });
}

export default function Editor({ mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const projectFolder = new URLSearchParams(location.search).get('folder');
  const { id } = useParams<{ id: string }>();
  // Word 导入的草稿（AI 解析结果）：进编辑器人工审核，保存才创建
  const imported = mode === 'new' ? (location.state as any)?.importedConfig : null;

  const [config, setConfig] = useState<SurveyConfig>(() =>
    mode === 'new'
      ? (imported ? { ...imported, id: imported.id || `survey-${Date.now().toString(36)}` } : blankSurvey(`survey-${Date.now().toString(36)}`))
      : blankSurvey('loading'),
  );
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('questions');
  const [dirty, setDirty] = useState(!!imported);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // 拖拽排序状态
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  // 0907 批量删除弹窗：按题目区间删除（从~到）；由题目菜单的「批量删除」触发
  const [batchRange, setBatchRange] = useState<{ qid: string } | null>(null);
  // 0909 批量移动弹窗：按 from/to 选连续区间 + 目标题 + 前面/后面；由题目菜单的「批量移动」触发
  const [batchMove, setBatchMove] = useState<{ qid: string } | null>(null);
  // 0904 二次确认弹窗：删除本页 / 批量删除都会用它
  const [confirmState, setConfirmState] = useState<{ title: string; body: React.ReactNode; confirmText?: string; danger?: boolean; onOk: () => void } | null>(null);

  // 编辑器内直接导入 Word（也兼容问卷管理页经 sessionStorage 传来的草稿）
  const [wordImporting, setWordImporting] = useState(false);
  const wordFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode !== 'new' || imported) return;
    try {
      const draft = window.sessionStorage.getItem('wenjuan_import_draft');
      if (draft) {
        const parsed = JSON.parse(draft) as SurveyConfig;
        setConfig({ ...parsed, id: parsed.id || `survey-${Date.now().toString(36)}` });
        setDirty(true);
        setSaveMsg('已导入 Word 草稿，请审核后保存');
      }
      window.sessionStorage.removeItem('wenjuan_import_draft');
    } catch { /* 草稿损坏则忽略，从空白问卷开始 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleWordImport = async (file: File) => {
    if (!/\.docx$/i.test(file.name)) { setError('请选择 .docx 文件（旧版 .doc 请先另存为 .docx）'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Word 文件不能超过 5MB'); return; }
    setWordImporting(true);
    setError(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('文件读取失败'));
        reader.onerror = () => reject(new Error('文件读取失败'));
        reader.readAsDataURL(file);
      });
      const result = await importDocAsSurvey(dataUrl);
      setConfig({ ...result.config, id: result.config?.id || `survey-${Date.now().toString(36)}` });
      setLoadedId(null);
      setDirty(true);
      setSaveMsg(result.warnings?.length ? `已导入草稿（${result.warnings.length} 个提示），请审核后保存` : '已导入 Word 草稿，请审核后保存');
      setTab('questions');
    } catch (e: any) {
      const err = e?.body?.error;
      setError(err === 'ai_not_configured' ? '服务器未配置 AI 密钥' : err === 'docx_parse_failed' ? '文档解析失败，请确认是有效的 .docx' : e?.body?.detail || e?.message || 'Word 导入失败');
    } finally {
      setWordImporting(false);
      if (wordFileRef.current) wordFileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    getSurvey(id)
      .then((data) => {
        setConfig(data.config);
        setLoadedId(data.config.id);
      })
      .catch((e: Error) => setError(e.message));
  }, [mode, id]);

  const updateConfig = (patch: Partial<SurveyConfig>) => {
    setConfig((c) => ({ ...c, ...patch }));
    setDirty(true);
  };

  const updateQuestion = (qid: string, patch: Partial<Question>) => {
    setConfig((c) => {
      const prev = c.questions.find((q) => q.id === qid);
      let questions = c.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q));
      // 0907 选项改名 / 删除时同步其它题里的选项关联、题目关联引用
      if (prev && patch.options) questions = syncOptionRefs(questions, qid, optionValuesOf(prev.options), optionValuesOf(patch.options));
      return { ...c, questions };
    });
    setDirty(true);
  };

  const addQuestion = (type: QuestionType) => {
    setConfig((c) => {
      const qid = nextQuestionId(c);
      const q = makeQuestion(type, qid);
      return { ...c, questions: [...c.questions, q] };
    });
    setDirty(true);
  };

  const insertBelow = (qid: string, type: QuestionType) => {
    setConfig((c) => {
      const idx = c.questions.findIndex((q) => q.id === qid);
      if (idx < 0) return c;
      const qidNew = nextQuestionId(c);
      const q = makeQuestion(type, qidNew);
      const qs = [...c.questions];
      qs.splice(idx + 1, 0, q);
      return { ...c, questions: qs };
    });
    setDirty(true);
  };

  // 0902 复制题目：在下方生成一道一模一样的题（新 id；显示逻辑引用保持指向原来的题）
  const duplicateQuestion = (qid: string) => {
    setConfig((c) => {
      const idx = c.questions.findIndex((q) => q.id === qid);
      if (idx < 0) return c;
      const copy = cloneQuestion(c.questions[idx], nextQuestionId(c));
      const qs = [...c.questions];
      qs.splice(idx + 1, 0, copy);
      return { ...c, questions: qs };
    });
    setDirty(true);
  };

  // 0902 复制本页题目：把分页栏之前那一页的全部题目复制一份，作为新的一页插在分页栏之后
  //（后面还有内容时再补一个分页栏隔开原来的下一页；页内互相引用的显示逻辑改为指向副本）
  const duplicatePage = (pagebreakId: string) => {
    setConfig((c) => {
      const idx = c.questions.findIndex((q) => q.id === pagebreakId);
      if (idx < 0 || c.questions[idx].type !== 'pagebreak') return c;
      const [start, end] = pageRangeBefore(c.questions, idx);
      const source = c.questions.slice(start, end);
      if (source.length === 0) return c;
      const ids = new Set(c.questions.map((q) => q.id));
      const idMap = new Map<string, string>();
      const copies = source.map((q) => {
        const newId = nextQuestionIdFrom(ids);
        ids.add(newId);
        idMap.set(q.id, newId);
        return cloneQuestion(q, newId);
      });
      for (const q of copies) {
        for (const cond of q.displayLogic?.conditions ?? []) {
          const mapped = idMap.get(cond.question);
          if (mapped) cond.question = mapped;
        }
        // 页内互相引用的选项关联 / 题目关联同样改为指向副本
        for (const l of q.optionLinks ?? []) {
          const mapped = idMap.get(l.questionId);
          if (mapped) l.questionId = mapped;
        }
        for (const c of q.correlation ?? []) {
          const mapped = idMap.get(c.questionId);
          if (mapped) c.questionId = mapped;
        }
      }
      const qs = [...c.questions];
      const insert: Question[] = [...copies];
      if (idx + 1 < qs.length) insert.push(makeQuestion('pagebreak', nextQuestionIdFrom(ids)));
      qs.splice(idx + 1, 0, ...insert);
      return { ...c, questions: qs };
    });
    setDirty(true);
  };

  const removeQuestion = (qid: string) => {
    setConfig((c) => {
      const removedIds = new Set([qid]);
      const qs = c.questions.filter((q) => q.id !== qid);
      return { ...c, questions: qs.map((q) => dropQuestionRefs(q, removedIds)) };
    });
    setDirty(true);
  };

  // 0904 删除本页题目：分页栏自身保留，只删它之前的整页题目
  //（pageRangeBefore 给出 [start, pagebreakIndex)；分页栏之后的题目与引用顺延）
  const removePageQuestions = (pagebreakId: string) => {
    setConfig((c) => {
      const idx = c.questions.findIndex((q) => q.id === pagebreakId);
      if (idx < 0 || c.questions[idx].type !== 'pagebreak') return c;
      const [start, end] = pageRangeBefore(c.questions, idx);
      const removedIds = new Set(c.questions.slice(start, end).map((q) => q.id));
      if (removedIds.size === 0) return c;
      const qs = c.questions.filter((q) => !removedIds.has(q.id));
      // 清理其他题对被删题的引用（显示逻辑 / 题目关联 / 选项关联 / 跳题目标）
      return { ...c, questions: qs.map((q) => dropQuestionRefs(q, removedIds)) };
    });
    setDirty(true);
  };

  // 0904 批量删除：一次删多道题（与 removeQuestion 同样清理引用）
  const batchRemoveQuestions = (ids: string[]) => {
    const removedIds = new Set(ids);
    setConfig((c) => {
      const qs = c.questions.filter((q) => !removedIds.has(q.id));
      return { ...c, questions: qs.map((q) => dropQuestionRefs(q, removedIds)) };
    });
    setDirty(true);
  };

  // 0904 弹「删除本页」确认弹窗——先把要删的题目列出来再让用户确认
  const askRemovePage = (pagebreakId: string) => {
    const idx = config.questions.findIndex((q) => q.id === pagebreakId);
    if (idx < 0 || config.questions[idx].type !== 'pagebreak') return;
    const [start, end] = pageRangeBefore(config.questions, idx);
    const targets = config.questions.slice(start, end);
    if (targets.length === 0) {
      setConfirmState({
        title: '本页没有可删除的题目',
        body: <p>此分页栏之前没有任何题目，无需删除。</p>,
        onOk: () => setConfirmState(null),
      });
      return;
    }
    const preview = targets.slice(0, 3).map((q, i) => (
      <li key={q.id}>{i + 1}. 「{titleText(q.title) || `第 ${start + i + 1} 题`}」</li>
    ));
    setConfirmState({
      title: `删除本页题目（共 ${targets.length} 题）`,
      danger: true,
      confirmText: `删除 ${targets.length} 题`,
      body: (
        <div className="submit-confirm">
          <p>将删除此分页栏之前的 <strong>{targets.length}</strong> 道题目（分页栏本身保留）：</p>
          <ul style={{ margin: '6px 0 6px 18px', padding: 0 }}>{preview}{targets.length > 3 && <li>… 等共 {targets.length} 题</li>}</ul>
          <p style={{ color: 'var(--danger, #e5484d)' }}>该操作不可撤销，被删题在其他题的显示逻辑中的引用也会被一并清除。</p>
        </div>
      ),
      onOk: () => {
        removePageQuestions(pagebreakId);
        setConfirmState(null);
      },
    });
  };

  // 0907 题目菜单「批量删除」：打开按区间删除弹窗（弹窗内选 from/to + 二次确认）
  const askBatchDeleteByRange = (qid: string) => {
    setBatchRange({ qid });
  };
  // 0907 区间删除弹窗点「删除」：弹二次确认，确认后删 [from, to] 全部题目 + 清理引用
  const confirmBatchDeleteByRange = (fromId: string, toId: string) => {
    const fromIdx = config.questions.findIndex((q) => q.id === fromId);
    const toIdx = config.questions.findIndex((q) => q.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [a, b] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
    const ids = config.questions.slice(a, b + 1).map((q) => q.id);
    const idToQ = new Map(config.questions.map((q) => [q.id, q]));
    const dmap = displayIndexMap(config.questions);
    const fromNo = dmap.get(fromId) ?? 0;
    const toNo = dmap.get(toId) ?? 0;
    const loNo = Math.min(fromNo || 0, toNo || 0);
    const hiNo = Math.max(fromNo || 0, toNo || 0);
    setConfirmState({
      title: '确定删除区间内全部题目？',
      danger: true,
      confirmText: '删除',
      body: (
        <div className="submit-confirm">
          <p>将删除 <strong>第 {loNo} 题</strong> 到 <strong>第 {hiNo} 题</strong> 之间的 <strong>{ids.length}</strong> 道题目（删除后不可恢复）：</p>
          <ul style={{ margin: '6px 0 6px 18px', padding: 0, maxHeight: 180, overflow: 'auto' }}>
            {ids.slice(0, 5).map((id) => {
              const q = idToQ.get(id);
              const no = dmap.get(id) ?? 0;
              return <li key={id}>{no ? `第 ${no} 题` : '标题/分页'}：{titleText(q?.title) || '（无标题）'}</li>;
            })}
            {ids.length > 5 && <li>… 等共 {ids.length} 题</li>}
          </ul>
          <p style={{ color: 'var(--danger, #e5484d)' }}>被删题在其他题的显示逻辑中的引用也会被一并清除。</p>
        </div>
      ),
      onOk: () => {
        batchRemoveQuestions(ids);
        setBatchRange(null);
        setConfirmState(null);
      },
    });
  };

  // 0909 题目菜单「批量移动」：打开按区间移动弹窗（弹窗内 from/to + 目标 + 前/后）
  const askBatchMoveByRange = (qid: string) => {
    setBatchMove({ qid });
  };
  // 0909 区间移动弹窗点「确定」：把 [from, to] 区间内的题目整段移到目标题目的「前面 / 后面」
  // - 用 array 索引算范围（含 from/to 之间的 statement / pagebreak 一并搬走，与批量删除同口径）
  // - 目标必须在 [from, to] 区间外；位移保持目标的最终相对位置（前面 / 后面）
  const performBatchMoveByRange = (fromId: string, toId: string, targetId: string, position: 'before' | 'after') => {
    setConfig((c) => {
      const fromIdx = c.questions.findIndex((q) => q.id === fromId);
      const toIdx = c.questions.findIndex((q) => q.id === toId);
      const targetIdx = c.questions.findIndex((q) => q.id === targetId);
      if (fromIdx < 0 || toIdx < 0 || targetIdx < 0) return c;
      const [a, b] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      if (targetIdx >= a && targetIdx <= b) return c; // 目标在区间内，无意义
      const moved = c.questions.slice(a, b + 1);
      const remaining = c.questions.filter((_, i) => i < a || i > b);
      const newTargetIdx = remaining.findIndex((q) => q.id === targetId);
      if (newTargetIdx < 0) return c;
      const insertAt = position === 'before' ? newTargetIdx : newTargetIdx + 1;
      remaining.splice(insertAt, 0, ...moved);
      return { ...c, questions: remaining };
    });
    setDirty(true);
    setBatchMove(null);
  };

  // 拖拽排序
  const handleDragStart = (_e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
  };
  const handleDragOver = (_e: React.DragEvent, idx: number) => {
    if (dragIndex === null) return;
    setOverIndex(idx);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };
  const handleDrop = (_e: React.DragEvent, idx: number) => {
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    setConfig((c) => {
      const qs = [...c.questions];
      const [moved] = qs.splice(dragIndex, 1);
      qs.splice(idx, 0, moved);
      return { ...c, questions: qs };
    });
    setDirty(true);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleSave = async (): Promise<boolean> => {
    setSaving(true);
    setSaveMsg(null);
    try {
      if (mode === 'new' || !loadedId) {
        await createSurvey(config, projectFolder);
        setLoadedId(config.id);
        setSaveMsg('已创建');
        navigate(`/editor/${encodeURIComponent(config.id)}${projectFolder ? `?folder=${encodeURIComponent(projectFolder)}` : ''}`, { replace: true });
      } else {
        await updateSurvey(loadedId, config);
        setSaveMsg('已保存');
      }
      setDirty(false);
      return true;
    } catch (e: any) {
      const body = e?.body;
      if (body?.error === 'invalid_config') {
        // 带上服务端第一条校验错误，方便定位是哪道题的哪个配置（此前只有一句「请检查题目内容」）
        const first = body?.validation?.errors?.[0];
        setSaveMsg(first ? `保存失败：${first.path} ${first.msg}` : '保存失败，请检查题目内容');
      } else if (body?.error === 'conflict') setSaveMsg('ID 已存在，请改 id');
      else setSaveMsg(body?.message || e?.message || '保存失败');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (saving) return;
    if (dirty || mode === 'new' || !loadedId) {
      const saved = await handleSave();
      if (!saved) return;
    }
    setPublishOpen(true);
  };

  return (
    <div className="editor-layout">
      <header className="topbar">
        <Link to={projectFolder ? `/folders?folder=${encodeURIComponent(projectFolder)}` : '/'} className="editor-back">← 返回{projectFolder ? '项目文件夹' : '问卷管理'}</Link>
        <Link to="/" className="brand">
          <div className="brand-mark">问</div>
          <div className="brand-name">编辑器</div>
        </Link>
        <div className="topbar-spacer" />

        <div className="editor-tabs" style={{ display: 'flex', gap: 4 }}>
          <button
            className={`q-chip ${tab === 'questions' ? 'is-on' : ''}`}
            onClick={() => setTab('questions')}
          >
            题目（{config.questions.length}）
          </button>
          <button
            className={`q-chip ${tab === 'preview' ? 'is-on' : ''}`}
            onClick={() => setTab('preview')}
          >
            预览
          </button>
        </div>

        <button
          className="btn"
          onClick={() => wordFileRef.current?.click()}
          disabled={wordImporting}
          title="上传 .docx，AI 解析为问卷草稿（覆盖当前编辑内容，保存后生效）"
        >
          {wordImporting ? 'AI 解析中…' : '⬆ 导入 Word'}
        </button>
        <input
          ref={wordFileRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleWordImport(f); }}
        />

        <button
          className="btn btn-primary"
          onClick={() => { void handleSave(); }}
          disabled={saving || !dirty}
          title={dirty ? '保存到 DB' : '无改动'}
        >
          {saving ? '保存中…' : dirty ? '保存' : '已保存'}
        </button>
        <button className="btn btn-publish" onClick={() => { void handlePublish(); }} disabled={saving}>
          ✈ 发布
        </button>

        {saveMsg && <span className="save-msg">{saveMsg}</span>}

        {/* 设置按钮：顶栏最右 */}
        <button
          className="icon-circle"
          onClick={() => setSettingsOpen(true)}
          title="设置"
          aria-label="设置"
        >
          <IconGear />
        </button>
      </header>

      {error && <div className="editor-error">加载失败：{error}</div>}
      {imported && (
        <div className="editor-error" style={{ background: 'var(--accent-soft-2, #eef4ff)', color: 'var(--accent-strong, #2f63d8)' }}>
          已由 Word 文档 AI 解析生成草稿，请逐题核对（题型/选项/分值/前置页/签名设置），确认无误后点「保存」正式创建。
        </div>
      )}

      {tab === 'questions' && (
        <main className="editor-page">
          <div className="editor-card">
            <div className="editor-head">
              <div className="editor-head-main">
                <input
                  className="editor-title"
                  value={config.header?.mainTitle ?? ''}
                  onChange={(e) =>
                    updateConfig({ header: { ...config.header, mainTitle: e.target.value } })
                  }
                  placeholder="问卷标题"
                />
                <input
                  className="editor-desc"
                  value={config.header?.subTitle ?? ''}
                  onChange={(e) =>
                    updateConfig({ header: { ...config.header, subTitle: e.target.value } })
                  }
                  placeholder="请输入描述"
                />
              </div>
            </div>

            <div className="q-list">
              {(() => {
                const pmap = pageNumberMap(config.questions);
                const dmap = displayIndexMap(config.questions);
                return config.questions.map((q, idx) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    index={idx}
                    displayIndex={dmap.get(q.id) ?? 0}
                    allQuestions={config.questions}
                    onChange={(patch) => updateQuestion(q.id, patch)}
                    onRemove={() => removeQuestion(q.id)}
                    onInsertBelow={(t) => insertBelow(q.id, t)}
                    onDuplicate={() => duplicateQuestion(q.id)}
                    onDuplicatePage={() => duplicatePage(q.id)}
                    onRemovePage={() => askRemovePage(q.id)}
                    pageNumber={pmap.get(q.id)}
                    draggable
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                    isDragOver={overIndex === idx && dragIndex !== null && dragIndex !== idx}
                    onAskBatchDelete={() => askBatchDeleteByRange(q.id)}
                    onAskBatchMove={() => askBatchMoveByRange(q.id)}
                  />
                ));
              })()}

              <div className="q-add">
                <button
                  type="button"
                  className="q-add-btn"
                  onClick={() => setAddOpen(true)}
                >
                  + 添加题目
                </button>
              </div>

            </div>
          </div>

          {addOpen && (
            <AddQuestionModal
              onSelect={(t) => {
                setAddOpen(false);
                addQuestion(t);
              }}
              onClose={() => setAddOpen(false)}
            />
          )}
        </main>
      )}

      {tab === 'preview' && (
        <main className="editor-page">
          <div className="editor-card">
            <PreviewPanel config={config} />
          </div>
        </main>
      )}

      {settingsOpen && (
        <SettingsPanel
          config={config}
          onChange={updateConfig}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {publishOpen && (
        <PublishModal
          surveyId={config.id}
          onClose={() => setPublishOpen(false)}
        />
      )}
      {confirmState && (
        <ConfirmDialog
          title={confirmState.title}
          confirmText={confirmState.confirmText}
          danger={confirmState.danger}
          body={confirmState.body}
          onOk={confirmState.onOk}
          onCancel={() => setConfirmState(null)}
        />
      )}
      {batchRange && (
        <BatchDeleteByRangeDialog
          qid={batchRange.qid}
          questions={config.questions}
          onClose={() => setBatchRange(null)}
          onConfirm={confirmBatchDeleteByRange}
        />
      )}
      {batchMove && (
        <BatchMoveByRangeDialog
          qid={batchMove.qid}
          questions={config.questions}
          onClose={() => setBatchMove(null)}
          onConfirm={performBatchMoveByRange}
        />
      )}
    </div>
  );
}

/** 0907 批量删除弹窗：标题区展示当前题目，按 from/to 下拉选区间，确认后弹二次确认 */
function BatchDeleteByRangeDialog({
  qid,
  questions,
  onClose,
  onConfirm,
}: {
  qid: string;
  questions: Question[];
  onClose: () => void;
  onConfirm: (fromId: string, toId: string) => void;
}) {
  const dmap = displayIndexMap(questions);
  // 只把可作答题放进下拉（statement/pagebreak 不算题号，也不参与区间）
  const options = questions
    .map((q) => ({ q, no: dmap.get(q.id) ?? 0 }))
    .filter(({ no, q }) => no > 0 && q.type !== 'statement' && q.type !== 'pagebreak')
    .map(({ q, no }) => ({ id: q.id, no, title: titleText(q.title) || `第 ${no} 题` }));
  const [from, setFrom] = useState<string>(qid);
  const [to, setTo] = useState<string>(qid);
  const fromNo = options.find((o) => o.id === from)?.no ?? 0;
  const toNo = options.find((o) => o.id === to)?.no ?? 0;
  const valid = toNo >= fromNo;
  const current = options.find((o) => o.id === qid);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="record-modal confirm-modal batch-range-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <h2>批量删除题目</h2>
          <button className="modal-close" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <div className="batch-range-body">
          <div className="batch-range-row">
            <span className="batch-range-label">当前题目：</span>
            <span className="batch-range-current">
              {current ? `${current.no}. ${current.title}` : '（无）'}
            </span>
          </div>
          <div className="batch-range-label-row">批量删除题目：</div>
          <div className="batch-range-row">
            <span className="batch-range-kw">从</span>
            <select className="input" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="从">
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.no}. {o.title}</option>
              ))}
            </select>
          </div>
          <div className="batch-range-row">
            <span className="batch-range-kw">到</span>
            <select className="input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="到">
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.no}. {o.title}</option>
              ))}
            </select>
          </div>
          {!valid && <p className="batch-range-hint">「到」的题号必须大于等于「从」</p>}
        </div>
        <div className="confirm-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>关闭</button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => onConfirm(from, to)}
            disabled={!valid}
            autoFocus
          >
            删除
          </button>
        </div>
      </div>
    </div>
  );
}

/** 0909 批量移动弹窗：from/to 选连续区间 + 目标题 + 前面/后面，整体迁移；目标必须在区间外
 *  沿用 BatchDeleteByRangeDialog 的题号口径（displayIndexMap 排除 statement/pagebreak） */
function BatchMoveByRangeDialog({
  qid,
  questions,
  onClose,
  onConfirm,
}: {
  qid: string;
  questions: Question[];
  onClose: () => void;
  onConfirm: (fromId: string, toId: string, targetId: string, position: 'before' | 'after') => void;
}) {
  const dmap = displayIndexMap(questions);
  // 只把可作答题放进下拉（statement/pagebreak 不算题号，也不参与区间）；from/to/target 三组共用同一选项列表
  const options = questions
    .map((q) => ({ q, no: dmap.get(q.id) ?? 0 }))
    .filter(({ no, q }) => no > 0 && q.type !== 'statement' && q.type !== 'pagebreak')
    .map(({ q, no }) => ({ id: q.id, no, title: titleText(q.title) || `第 ${no} 题` }));
  const [from, setFrom] = useState<string>(qid);
  const [to, setTo] = useState<string>(qid);
  // 目标默认挑一个不同于当前题目的题（避免一开始就落在区间内弹错误）；只有一道题时只能用当前题作为目标，会被校验拦下
  const initialTarget = options.find((o) => o.id !== qid)?.id ?? options[0]?.id ?? '';
  const [target, setTarget] = useState<string>(initialTarget);
  const [position, setPosition] = useState<'before' | 'after'>('before');

  const fromNo = options.find((o) => o.id === from)?.no ?? 0;
  const toNo = options.find((o) => o.id === to)?.no ?? 0;
  const validRange = toNo >= fromNo;
  const lo = Math.min(fromNo, toNo);
  const hi = Math.max(fromNo, toNo);
  const targetNo = options.find((o) => o.id === target)?.no ?? 0;
  // 目标若题号落在 [lo, hi] 内则无效；区间内可能夹了 statement/pagebreak，校验用题号比 array 索引更直观
  const targetInRange = !!target && targetNo >= lo && targetNo <= hi;
  const validTarget = !!target && !targetInRange;
  const valid = validRange && validTarget && options.length > 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="record-modal confirm-modal batch-range-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <h2>批量移动题目</h2>
          <button className="modal-close" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <div className="batch-range-body">
          <div className="batch-range-label-row">选择需要移动的题目段：</div>
          <div className="batch-range-row">
            <span className="batch-range-kw">从</span>
            <select className="input" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="从">
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.no}. {o.title}</option>
              ))}
            </select>
          </div>
          <div className="batch-range-row">
            <span className="batch-range-kw">到</span>
            <select className="input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="到">
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.no}. {o.title}</option>
              ))}
            </select>
          </div>
          {!validRange && <p className="batch-range-hint">「到」的题号必须大于等于「从」</p>}
          <div className="batch-range-row">
            <span className="batch-range-kw">移至</span>
            <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} aria-label="移至">
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.no}. {o.title}</option>
              ))}
            </select>
            {/* 0909 位置单选：内联 segmented control（前面 / 后面）；视觉对齐截图 */}
            <div className="batch-range-pos" role="radiogroup" aria-label="移至位置">
              <label className={`batch-range-pos-opt${position === 'before' ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="batch-move-pos"
                  checked={position === 'before'}
                  onChange={() => setPosition('before')}
                />
                前面
              </label>
              <label className={`batch-range-pos-opt${position === 'after' ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="batch-move-pos"
                  checked={position === 'after'}
                  onChange={() => setPosition('after')}
                />
                后面
              </label>
            </div>
          </div>
          {targetInRange && <p className="batch-range-hint">目标题目不能在被移动的区间内</p>}
        </div>
        <div className="confirm-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>关闭</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(from, to, target, position)}
            disabled={!valid}
            autoFocus
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
}

/** 0904 通用二次确认弹窗：删除本页 / 批量删除共用 */
function ConfirmDialog({
  title,
  body,
  confirmText,
  danger,
  onOk,
  onCancel,
}: {
  title: string;
  body?: React.ReactNode;
  confirmText?: string;
  danger?: boolean;
  onOk: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="record-modal confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onCancel} aria-label="关闭">×</button>
        </div>
        <div className="confirm-modal-body">{body}</div>
        <div className="confirm-actions">
          <button className="btn btn-ghost" onClick={onCancel}>取消</button>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={onOk}
            autoFocus
          >
            {confirmText ?? '确认'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PublishModal({ surveyId, onClose }: { surveyId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/s/${encodeURIComponent(surveyId)}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(link)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="record-modal publish-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div>
            <div className="eyebrow">PUBLISH QUESTIONNAIRE</div>
            <h2>发布成功</h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <p className="publish-hint">复制填写链接，或让填写者扫描二维码。</p>
        <label className="publish-label">填写链接</label>
        <div className="publish-link-row">
          <input className="input" value={link} readOnly onFocus={(e) => e.currentTarget.select()} />
          <button className="btn btn-primary" onClick={() => { void copyLink(); }}>{copied ? '已复制' : '复制链接'}</button>
        </div>
        <div className="publish-qr-block">
          <div className="publish-label">二维码</div>
          <div className="publish-qr-frame"><img src={qrUrl} alt="问卷填写二维码" /></div>
        </div>
      </div>
    </div>
  );
}

function IconGear() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
